import styled from "styled-components";
import { useState } from "react";
import { usePage } from "@inertiajs/react";

import Media from "../_kit/Media";
import Lightbox from "./Lightbox";
import {
  Section,
  SectionHeader,
  Lede,
  Heading,
  Eyebrow,
  Container,
} from "../_kit/Section";
import { useLandingLanguage } from "../../../contexts/landing-language";
import {
  type,
  tracking,
  radius,
  motion,
  media,
  font,
  color,
} from "../../../styles/tokens";

/* One grid of real images.
 *
 * Each cell used to be its OWN auto-rotating slideshow on a duplicated 5s interval — a fourth
 * carousel implementation on the page — painted as a CSS background-image, so nothing had alt text
 * and every cell loaded a full-resolution original into a 313px box.
 */

const GalleryGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(4, minmax(0, 1fr));
  gap: 16px;

  ${media.lg`
    grid-template-columns: repeat(3, minmax(0, 1fr));
  `}

  ${media.md`
    /* Two columns rather than one, so the is_big composition an editor arranged survives on a
     * phone instead of collapsing into an undifferentiated stack. */
    grid-template-columns: repeat(2, minmax(0, 1fr));
    gap: 10px;
  `}
`;

const Cell = styled.button`
  position: relative;
  display: block;
  padding: 0;
  border: 0;
  background: none;
  cursor: pointer;
  overflow: hidden;
  border-radius: ${radius.md};
  grid-column: span ${(props) => (props.$big ? 2 : 1)};
  grid-row: span ${(props) => (props.$big ? 2 : 1)};

  > div {
    height: 100%;
    transition: transform ${motion.slow};
  }

  &:hover > div {
    transform: scale(1.04);
  }

  &::after {
    content: "";
    position: absolute;
    inset: 0;
    background: ${color.scrimSoft};
    opacity: 0;
    transition: opacity ${motion.base};
  }

  &:hover::after {
    opacity: 1;
  }
`;

const Caption = styled.span`
  position: absolute;
  left: 0;
  right: 0;
  bottom: 0;
  z-index: 1;
  display: flex;
  flex-direction: column;
  gap: 2px;
  padding: 32px 16px 14px;
  text-align: left;
  background: ${color.scrimSoft};
  color: ${color.onDark};

  span {
    font-family: ${font.display};
    font-size: ${type.h4};
    line-height: 1.15;
  }

  small {
    font-size: ${type.xs};
    letter-spacing: ${tracking.wide};
    text-transform: uppercase;
    color: ${color.onDarkMuted};
  }
`;

const Gallery = (props) => {
  const { galleries } = usePage().props;
  const { t } = useLandingLanguage();
  const copy = t.gallery;
  const [openGroup, setOpenGroup] = useState(null);

  /* ONE tile per gallery group, not one per image.
   *
   * Editors organise the gallery into named sets — "The Rooftop Glamping Ace", "Hebe Cafe" — and
   * that structure is the content. Rendering every image as its own tile threw it away and, with
   * the 94 images actually in the CMS, produced roughly ten thousand pixels of undifferentiated
   * grid. The tile is the set; the lightbox is where its photos live.
   */
  const groups = (galleries || [])
    .map((group) => ({
      name: group.name,
      big: group.is_big === true,
      images: group.images || [],
      cover: (group.images || [])[0],
    }))
    .filter((group) => group.cover);

  const active = openGroup === null ? null : groups[openGroup];

  return (
    <Section id="gallery" $tone="alt">
      <Container $width="wide">
        <SectionHeader>
          <Eyebrow>{copy.subtitle}</Eyebrow>
          <Heading>{copy.title}</Heading>
          <Lede>{copy.description}</Lede>
        </SectionHeader>

        <GalleryGrid>
          {groups.map((group, index) => (
            <Cell
              key={group.name}
              type="button"
              $big={group.big}
              aria-label={`${group.name} — ${group.images.length} ${copy.photos}`}
              onClick={() => setOpenGroup(index)}
            >
              <Media
                source={group.cover}
                alt={group.cover.alt_text || group.name}
                ratio={group.big ? "1 / 1" : "1 / 1"}
                sizes="(max-width: 768px) 50vw, 25vw"
              />
              <Caption>
                <span>{group.name}</span>
                <small>
                  {group.images.length} {copy.photos}
                </small>
              </Caption>
            </Cell>
          ))}
        </GalleryGrid>
      </Container>

      {active && (
        <Lightbox
          images={active.images}
          initialIndex={0}
          onClose={() => setOpenGroup(null)}
          label={active.name}
          closeLabel={copy.close}
          prevLabel={copy.prev}
          nextLabel={copy.next}
        />
      )}
    </Section>
  );
};

export default Gallery;
